import { useEffect, useMemo, useRef, useState } from "react";
import { Dialog, DialogTrigger, Heading, Modal, ModalOverlay, Pressable } from "react-aria-components";
import { AlarmClock, Bell, CheckCheck, LoaderCircle, Sparkles, X } from "lucide-react";
import { useNavigate } from "react-router";
import { cn } from "@/utils/helpers";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { useWorkspaceStore } from "@/stores/workspaceStore";
import { useCoShellPortal } from "../CoShellPortalContext";
import { KitPopover } from "./kit/Popover";
import { KitTabIndicator } from "./kit/TabIndicator";
import {
  useNotificationList,
  useNotificationOrganizationId,
  useNotificationReads,
  useUnreadNotificationCount,
  type NotificationItem,
} from "../api/useNotifications";

/*
 * The notification centre (CreativeOS Library.lib.pen, Notification panel,
 * Notification row (+ Loading), Notification · Loading more / End line;
 * chapter 08 frames V1Cpr, ML2MB, rDeDP). Floating chrome, radius 16, 374
 * wide. On a desk it is a popover beside the rail's bell; on a phone or a
 * tablet it is a sheet 8px in from the sides and the foot, over a scrim.
 *
 * Figma 08 "the notification centre, open": All, Comments, Approvals and
 * Mentions; each line names the brand and the post, says what happened in one
 * sentence, and opens that post. The late-answer and Creo rows, the per-row
 * actions and the settings button have no data yet, so they are not drawn.
 */

type Tab = "all" | "comments" | "approvals" | "mentions";

const COMMENT_KINDS = new Set(["comment"]);
const MENTION_KINDS = new Set(["mention"]);

/** Asked for, or given: the approvals loop. */
const APPROVAL_KINDS = new Set([
  "concept_review_requested",
  "brief_review_requested",
  "concept_approved",
  "brief_approved",
  "concept_changes_requested",
  "brief_changes_requested",
]);

const WHAT: Record<string, string> = {
  concept: "the concept",
  brief: "the creative brief",
};

/** "Approved the idea for X." → X (the backend's sentence; null when it is something else). */
function postTitleFrom(excerpt: string): string | null {
  const match = /^(?:Approved|Asked for changes to) the (?:idea|creative brief) for (.+)\.$/.exec(excerpt.trim());
  return match?.[1] ?? null;
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return `${parts[0]![0]}${parts[parts.length - 1]![0]}`.toUpperCase();
}

export interface NotificationWords {
  title: string;
  body: string | null;
  /** A system notice takes an icon box instead of the actor's initials. */
  system: boolean;
  /** A system notice's mark: Creo's, or the alarm for what went wrong. */
  tone?: "creo" | "live" | "alert";
}

/** "Tracing the Bedouin roots of…": a post's title, short enough for one line. */
function shortTitle(title: string): string {
  const clean = title.trim();
  return clean.length > 30 ? `${clean.slice(0, 29).trimEnd()}…` : clean;
}

const firstName = (name: string) => name.trim().split(/\s+/)[0] || name.trim();

/** The row's sentence, from the kind, the actor, the brand and the excerpt. */
export function notificationWords(item: NotificationItem): NotificationWords {
  const actor = item.actorName.trim() || "Someone";
  const brand = item.brandName.trim();
  const from = brand ? `${actor} from ${brand}` : actor;
  const excerpt = item.excerpt.trim();
  const post = postTitleFrom(excerpt);
  // Figma 08: "Fatafeat · Tracing the Bedouin roots of…", then what happened.
  const slotTitle = item.slotTitle?.trim();
  if (slotTitle || item.kind === "daily_digest") {
    const title = slotTitle ? `${brand || actor} · ${shortTitle(slotTitle)}` : brand || actor;
    switch (item.kind) {
      case "concept_approved":
      case "brief_approved":
        return { title, body: `Approved by ${firstName(actor)}.`, system: false };
      case "concept_changes_requested":
      case "brief_changes_requested":
        return { title, body: post || !excerpt ? `${firstName(actor)} asked for changes.` : `${firstName(actor)} asked for changes: “${excerpt}”`, system: false };
      case "comment":
        return { title, body: `${firstName(actor)}: “${excerpt}”`, system: false };
      case "mention":
        return { title, body: `${firstName(actor)} mentioned you: “${excerpt}”`, system: false };
      case "client_opened":
      case "concept_review_requested":
      case "brief_review_requested":
        return { title, body: excerpt || null, system: false };
      case "publish_failed":
      case "brief_not_accepted":
        return { title, body: excerpt || null, system: true, tone: "alert" };
      case "post_live":
        return { title, body: excerpt || null, system: true, tone: "live" };
      default:
        return { title, body: excerpt || null, system: true, tone: "creo" };
    }
  }
  switch (item.kind) {
    case "concept_approved":
    case "brief_approved": {
      const part = item.kind === "concept_approved" ? "Concept." : "Creative brief.";
      return { title: post ? `${from} approved ${post}` : `${from} approved ${WHAT[item.kind.split("_")[0]!]}`, body: part, system: false };
    }
    case "concept_changes_requested":
    case "brief_changes_requested": {
      const what = WHAT[item.kind.split("_")[0]!];
      const title = post ? `${from} asked for changes to ${post}` : `${from} asked for changes to ${what}`;
      // The excerpt is the client's reason when they gave one.
      return { title, body: post || !excerpt ? (item.kind === "concept_changes_requested" ? "Concept." : "Creative brief.") : `“${excerpt}”`, system: false };
    }
    case "brief_not_accepted":
      return { title: "A creative brief is still not approved", body: excerpt || null, system: true };
    case "concept_review_requested":
    case "brief_review_requested":
      return { title: excerpt || `${actor} sent posts for your approval`, body: brand ? `From ${actor}, ${brand}.` : null, system: false };
    default:
      return { title: excerpt || "Notification", body: null, system: false };
  }
}

type Group = "Today" | "Yesterday" | "Earlier";

function dayKey(date: Date): string {
  return `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
}

function groupOf(iso: string, now: Date): Group {
  const date = new Date(iso);
  if (dayKey(date) === dayKey(now)) return "Today";
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (dayKey(date) === dayKey(yesterday)) return "Yesterday";
  return "Earlier";
}

function clock(date: Date): string {
  return date.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", hour12: false });
}

/** "16:20" today and yesterday, "Mon 30 Nov" before that (Figma 08). */
function metaTime(iso: string, group: Group): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  if (group !== "Earlier") return clock(date);
  const weekday = date.toLocaleDateString("en-GB", { weekday: "short" });
  const month = date.toLocaleDateString("en-GB", { month: "short" }).replace("Sept", "Sep");
  return `${weekday} ${date.getDate()} ${month}`;
}

/** Notification row: 24 mark, the words, the unread dot. Unread rows take the hover fill. */
/** Reveal order for the panel: each heading and row appears 30ms after the last, capped so a long list does not crawl. */
const TAB_COUNT = 4;
const stagger = (i: number) => ({ "--i": Math.min(i, 14) }) as React.CSSProperties;

function NotificationRow({ item, group, index, onOpen }: { item: NotificationItem; group: Group; index: number; onOpen: () => void }) {
  const words = notificationWords(item);
  const unread = !item.readAt;
  const time = metaTime(item.createdAt, group);
  return (
    <li className="co-notif-in" style={stagger(index)}>
      <button
        type="button"
        onClick={onOpen}
        aria-label={`${unread ? "Unread. " : ""}${words.title}. ${words.body ?? ""} ${time}`}
        className={cn(
          "flex w-full items-start gap-3 rounded-tip p-3 text-left outline-none transition-colors duration-150",
          "hover:bg-co-hover focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-co-accent",
          unread && "bg-co-hover",
        )}
      >
        {words.system ? (
          <span aria-hidden className="grid size-6 shrink-0 place-items-center rounded-[4px] bg-co-tonal">
            {words.tone === "alert" || !words.tone ? (
              <AlarmClock className="size-3.5 text-co-alert-ink" strokeWidth={1.75} />
            ) : (
              <Sparkles className={cn("size-3.5", words.tone === "live" ? "text-co-live" : "text-co-accent-ink")} strokeWidth={1.75} />
            )}
          </span>
        ) : (
          <span
            aria-hidden
            className="grid size-6 shrink-0 place-items-center rounded-full bg-co-tonal text-[12px] font-bold leading-none text-co-text2"
          >
            {initials(item.actorName)}
          </span>
        )}
        <span className="flex min-w-0 flex-1 flex-col gap-1">
          <span className="text-[14px] font-semibold leading-[1.35] text-co-text">{words.title}</span>
          {words.body ? <span className="text-[13px] leading-[1.4] text-co-text2">{words.body}</span> : null}
          <span className="text-[12px] leading-[1.4] text-co-muted">{time}</span>
        </span>
        {unread ? <span aria-hidden className="mt-1.5 size-[7px] shrink-0 rounded-full bg-co-accent" /> : null}
      </button>
    </li>
  );
}

/** Notification row · Loading. */
function LoadingRow() {
  return (
    <div aria-hidden className="flex items-center gap-3 rounded-row px-2.5 py-3">
      <span className="size-6 shrink-0 animate-pulse rounded-full bg-co-tonal" />
      <span className="flex flex-1 flex-col gap-2">
        <span className="h-3 w-[200px] max-w-full animate-pulse rounded-[6px] bg-co-tonal" />
        <span className="h-2.5 w-[140px] animate-pulse rounded-[5px] bg-co-tonal" />
      </span>
    </div>
  );
}

function PanelTab({ label, count, picked, index, onPress }: { label: string; count: number; picked: boolean; index: number; onPress: () => void }) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={picked}
      onClick={onPress}
      style={stagger(index)}
      className={cn(
        "co-notif-in flex items-center gap-[5px] pb-2 text-[13px] leading-[1.4] outline-none transition-colors duration-200",
        "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-co-accent",
        picked ? "font-semibold text-co-text" : "font-medium text-co-text2 hover:text-co-text",
      )}
    >
      {label}
      {count > 0 ? (
        <span className={cn("text-[12px] font-normal", picked ? "text-co-accent-ink" : "text-co-muted")}>{count}</span>
      ) : null}
    </button>
  );
}

/**
 * The panel itself: head (title, Mark read, close, tabs) and the list in
 * Today, Yesterday and Earlier, paging older ones in as the list scrolls.
 */
export function NotificationPanel({ onClose, grabber = false }: { onClose: () => void; grabber?: boolean }) {
  const organizationId = useNotificationOrganizationId();
  const unread = useUnreadNotificationCount(organizationId);
  const list = useNotificationList(organizationId, true);
  const { markRead, markAllRead } = useNotificationReads(organizationId);
  const setSelectedBrandId = useWorkspaceStore((s) => s.setSelectedBrandId);
  const navigate = useNavigate();
  const [tab, setTab] = useState<Tab>("all");
  const [now] = useState(() => new Date());
  const sentinel = useRef<HTMLDivElement>(null);

  const items = useMemo(() => list.data?.pages.flatMap((page) => page.items) ?? [], [list.data]);
  const inTab = (item: NotificationItem, which: Tab) =>
    which === "all"
      ? true
      : which === "comments"
        ? COMMENT_KINDS.has(item.kind)
        : which === "mentions"
          ? MENTION_KINDS.has(item.kind)
          : APPROVAL_KINDS.has(item.kind);
  const unreadIn = (which: Tab) => items.filter((item) => inTab(item, which) && !item.readAt).length;
  const shown = items.filter((item) => inTab(item, tab));
  const groups = useMemo(() => {
    const out: { group: Group; items: NotificationItem[] }[] = [];
    for (const item of shown) {
      const group = groupOf(item.createdAt, now);
      const last = out[out.length - 1];
      if (last?.group === group) last.items.push(item);
      else out.push({ group, items: [item] });
    }
    return out;
  }, [shown, now]);

  // Older pages arrive as the end of the list scrolls into view.
  const { hasNextPage, isFetchingNextPage, fetchNextPage } = list;
  useEffect(() => {
    const node = sentinel.current;
    if (!node || !hasNextPage) return;
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting) && !isFetchingNextPage) void fetchNextPage();
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const openItem = (item: NotificationItem) => {
    onClose();
    if (!item.readAt) markRead.mutate(item.id);
    if (item.brandId) setSelectedBrandId(item.brandId);
    // The post itself, in its month, when the line knows it.
    if (item.slotId && item.planYear && item.planMonth) {
      navigate(
        `/dashboard/content-calendar?${new URLSearchParams({ year: String(item.planYear), month: String(item.planMonth), view: "concepts", slot: item.slotId })}`,
      );
      return;
    }
    navigate("/dashboard/content-calendar");
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {grabber ? (
        <div aria-hidden className="flex h-3 shrink-0 items-end justify-center">
          <span className="h-1 w-9 rounded-[2px] bg-co-faint" />
        </div>
      ) : null}
      <div className="flex shrink-0 flex-col gap-3 px-4 pt-2.5">
        <div className="flex items-center gap-2">
          <Heading slot="title" className="font-section text-[22px] font-bold leading-[1.1] text-co-text">
            Notifications
          </Heading>
          <span className="flex-1" />
          {unread > 0 ? (
            <button
              type="button"
              onClick={() => markAllRead.mutate()}
              className="flex h-8 items-center gap-1.5 rounded-pill px-1 text-[13px] font-medium text-co-muted outline-none transition-colors hover:text-co-text focus-visible:outline-2 focus-visible:outline-co-accent"
            >
              <CheckCheck aria-hidden className="size-3.5" strokeWidth={1.75} />
              Mark read
            </button>
          ) : null}
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            className="grid size-8 place-items-center rounded-full text-co-muted outline-none transition-colors hover:bg-co-hover hover:text-co-text focus-visible:outline-2 focus-visible:outline-co-accent"
          >
            <X aria-hidden className="size-3.5" strokeWidth={1.75} />
          </button>
        </div>
        <div role="tablist" aria-label="Show" className="relative flex gap-5">
          <PanelTab index={0} label="All" count={unread} picked={tab === "all"} onPress={() => setTab("all")} />
          <PanelTab index={1} label="Comments" count={unreadIn("comments")} picked={tab === "comments"} onPress={() => setTab("comments")} />
          <PanelTab index={2} label="Approvals" count={unreadIn("approvals")} picked={tab === "approvals"} onPress={() => setTab("approvals")} />
          <PanelTab index={3} label="Mentions" count={unreadIn("mentions")} picked={tab === "mentions"} onPress={() => setTab("mentions")} />
          <KitTabIndicator selectedKey={tab} className="bottom-0" />
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto px-1.5 pb-3 pt-1">
        {list.isPending ? (
          <div className="flex flex-col gap-0.5 pt-2" aria-label="Loading notifications">
            <LoadingRow />
            <LoadingRow />
            <LoadingRow />
            <LoadingRow />
          </div>
        ) : list.isError ? (
          <div className="flex flex-col items-center gap-2 px-2.5 py-10 text-center">
            <p className="text-[13px] text-co-muted">Notifications did not load.</p>
            <button
              type="button"
              onClick={() => void list.refetch()}
              className="rounded-pill px-2 text-[13px] font-medium text-co-accent-ink outline-none hover:underline focus-visible:outline-2 focus-visible:outline-co-accent"
            >
              Try again
            </button>
          </div>
        ) : shown.length === 0 ? (
          <p className="px-2.5 py-10 text-center text-[13px] text-co-muted">
            {tab === "all"
              ? "Nothing yet. Answers, comments and publishing news show up here."
              : tab === "comments"
                ? "No comments yet."
                : tab === "mentions"
                  ? "Nobody has mentioned you yet."
                  : "No approvals yet."}
          </p>
        ) : (
          <div className="flex flex-col gap-0.5">
            {groups.map(({ group, items: rows }, gi) => {
              const start = TAB_COUNT + groups.slice(0, gi).reduce((n, g) => n + 1 + g.items.length, 0);
              return (
                <section key={group} aria-label={group} className="flex flex-col gap-0.5">
                  <div role="heading" aria-level={3} className="co-notif-in px-2.5 pb-1.5 pt-2.5 text-[12.5px] font-normal leading-[1.4] text-co-muted" style={stagger(start)}>{group}</div>
                  <ul className="m-0 flex list-none flex-col gap-0.5 p-0">
                    {rows.map((item, ri) => (
                      <NotificationRow key={item.id} item={item} group={group} index={start + 1 + ri} onOpen={() => openItem(item)} />
                    ))}
                  </ul>
                </section>
              );
            })}
            {hasNextPage ? (
              <div ref={sentinel} className="flex items-center justify-center gap-2 px-2.5 py-3.5 text-[12px] text-co-muted">
                <LoaderCircle aria-hidden className="size-3.5 animate-spin" />
                Loading older notifications
              </div>
            ) : (
              <p className="px-2.5 pb-5 pt-4 text-center text-[12px] text-co-faint">That’s everything from the last 90 days.</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/** The phone and tablet form: a sheet 8px in, under the top lane, over the scrim. */
function NotificationSheet({ isOpen, onOpenChange }: { isOpen: boolean; onOpenChange: (open: boolean) => void }) {
  const portal = useCoShellPortal();
  const phone = useMediaQuery("(max-width: 767px)");
  return (
    <ModalOverlay
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      isDismissable
      UNSTABLE_portalContainer={portal ?? undefined}
      className={({ isEntering, isExiting }) =>
        cn(
          "fixed inset-0 z-[var(--z-index-modal)] bg-co-scrim",
          isEntering && "animate-in-smooth fade-in",
          isExiting && "animate-out-slow fade-out",
        )
      }
    >
      <Modal
        className={({ isEntering, isExiting }) =>
          cn(
            "co-glass-chrome fixed bottom-2 top-16 flex w-[374px] max-w-[calc(100vw-16px)] flex-col overflow-hidden rounded-panel text-co-text outline-none",
            phone ? "inset-x-2 mx-auto" : "right-2",
            isEntering && (phone ? "animate-in-smooth sheet-in" : "animate-in-smooth fade-in rise-in-12"),
            isExiting && (phone ? "animate-out-slow sheet-out" : "animate-out-slow fade-out sink-out-4"),
          )
        }
      >
        <Dialog aria-label="Notifications" className="flex min-h-0 flex-1 flex-col outline-none">
          <NotificationPanel grabber onClose={() => onOpenChange(false)} />
        </Dialog>
      </Modal>
    </ModalOverlay>
  );
}

function unreadLabel(unread: number): string {
  return unread === 0 ? "Notifications" : `Notifications, ${unread} unread`;
}

/**
 * The bell for the phone and tablet top lane (Bar / Top lane · Phone): a bare
 * 44px circle, the bell at 20 and the count badge. It opens the sheet.
 */
export function NotificationBellButton({ className }: { className?: string }) {
  const organizationId = useNotificationOrganizationId();
  const unread = useUnreadNotificationCount(organizationId);
  const [open, setOpen] = useState(false);
  if (!organizationId) return null;
  return (
    <>
      <button
        type="button"
        aria-label={unreadLabel(unread)}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => setOpen(true)}
        className={cn(
          "relative grid size-11 shrink-0 place-items-center rounded-full text-co-text outline-none transition-colors hover:bg-co-hover focus-visible:outline-2 focus-visible:outline-co-accent",
          className,
        )}
      >
        <Bell aria-hidden className="size-5" strokeWidth={1.6} />
        {unread > 0 ? (
          <span
            aria-hidden
            className="absolute left-6 top-1 grid h-4 min-w-[18px] place-items-center rounded-tag bg-co-accent px-1 text-[12px] font-bold leading-none text-co-on-accent"
          >
            {unread > 9 ? "9+" : unread}
          </span>
        ) : null}
      </button>
      <NotificationSheet isOpen={open} onOpenChange={setOpen} />
    </>
  );
}

type TipBind = (
  text: string,
) => Partial<{
  onMouseEnter: (e: React.MouseEvent<HTMLElement>) => void;
  onMouseLeave: () => void;
  onFocus: (e: React.FocusEvent<HTMLElement>) => void;
  onBlur: () => void;
}>;

/**
 * The bell in the rail foot (30px, a square count badge, off until something
 * is unread). Opens the notification centre beside the rail on a desk, and
 * the sheet on a phone or a tablet.
 */
export default function NotificationMenu({
  expanded,
  onNavigate,
  bindTip,
}: {
  expanded: boolean;
  onNavigate?: () => void;
  bindTip: TipBind;
}) {
  const organizationId = useNotificationOrganizationId();
  const unread = useUnreadNotificationCount(organizationId);
  const [open, setOpen] = useState(false);
  const desk = useMediaQuery("(min-width: 1024px)");

  if (!organizationId) return null;

  const close = () => {
    setOpen(false);
    onNavigate?.();
  };

  const trigger = (
    <button
      type="button"
      className="co-nav"
      aria-label={unreadLabel(unread)}
      aria-haspopup="dialog"
      aria-expanded={open}
      onClick={desk ? undefined : () => setOpen(true)}
      {...bindTip("Notifications")}
      style={{
        display: "flex",
        alignItems: "center",
        // The bell stays at 6 in a 30 row. Opening only adds the label.
        justifyContent: "flex-start",
        gap: 12,
        height: 30,
        padding: "0 6px",
        cursor: "pointer",
        fontFamily: "var(--font-body)",
        fontWeight: 600,
        fontSize: 13,
        letterSpacing: 0,
        textAlign: "left",
        width: expanded ? "100%" : 30,
        flex: "none",
        // Left to .co-nav (nav-ink, the hover pill); held on the hover pill while open.
        background: open ? "var(--co-nav-fill-hover)" : undefined,
        border: "none",
        position: "relative",
      }}
    >
      <Bell size={18} aria-hidden style={{ flex: "none" }} />
      {expanded ? (
        <span className="co-rail-text" style={{ flex: 1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>Notifications</span>
      ) : null}
      {/* The same round accent count as the collapsed bell (style B: a count is
          a capsule, never a square), pushed to the row end. */}
      {expanded && unread > 0 ? (
        <span
          aria-hidden="true"
          className="co-rail-text grid h-4 min-w-4 flex-none place-items-center rounded-full bg-co-accent px-[5px] font-mono text-[10px] font-bold leading-none text-co-on-accent"
        >
          {unread > 9 ? "9+" : unread}
        </span>
      ) : null}
      {!expanded && unread > 0 ? (
        <span
          aria-hidden="true"
          className="absolute grid h-4 min-w-4 place-items-center rounded-full bg-co-accent px-[5px] font-mono text-[10px] font-bold leading-none text-co-on-accent"
          style={{ left: 15, top: -3 }}
        >
          {unread > 9 ? "9+" : unread}
        </span>
      ) : null}
    </button>
  );

  if (!desk) {
    return (
      <>
        {trigger}
        <NotificationSheet isOpen={open} onOpenChange={(next) => (next ? setOpen(true) : close())} />
      </>
    );
  }

  return (
    <DialogTrigger isOpen={open} onOpenChange={setOpen}>
      <Pressable>{trigger}</Pressable>
      <KitPopover
        placement={expanded ? "top start" : "right bottom"}
        // The panel stands 12 off the rail's edge (the rail is 72, the bell 30 at its centre).
        offset={expanded ? 10 : 33}
        className="co-chrome-sheet flex h-[min(760px,calc(100dvh-24px))] w-[374px] max-w-[calc(100vw-2rem)] flex-col overflow-hidden !rounded-panel !border-0 p-0"
      >
        <Dialog aria-label="Notifications" className="flex min-h-0 flex-1 flex-col outline-none">
          <NotificationPanel onClose={close} />
        </Dialog>
      </KitPopover>
    </DialogTrigger>
  );
}
